'use client';

import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { cn } from '@/shadcn/lib/utils';
import { clientConfig } from '@/@core/config/client';
import 'mapbox-gl/dist/mapbox-gl.css';

const SLOVAKIA_BOUNDS: mapboxgl.LngLatBoundsLike = [
    [16.8, 47.7], // SW (lng, lat)
    [22.6, 49.7], // NE (lng, lat)
];

type Props = {
    className?: string | string[];
    height?: string;
    center?: [number, number]; // [lng, lat]
    zoom?: number;
    pitch?: number;
    bearing?: number;
    maxZoom?: number;
    onMapLoaded?: (map: mapboxgl.Map) => void;
};

export default function MapboxMap({
    height = '500px',
    center = [17.1077, 48.1486],
    zoom = 13,
    pitch = 25,
    bearing = -10,
    maxZoom = 15,
    className,
    onMapLoaded,
}: Props) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);

    useEffect(() => {
        if (!containerRef.current || mapRef.current) return;

        const token = clientConfig('mapbox.token');
        if (!token) {
            console.error('Missing mapbox.token in config');
            return;
        }

        mapboxgl.accessToken = token;

        const map = new mapboxgl.Map({
            container: containerRef.current,
            style: 'mapbox://styles/mapbox/standard',
            center,
            zoom,
            pitch,
            bearing,
            antialias: true,
            maxBounds: SLOVAKIA_BOUNDS,
            maxZoom,
            minZoom: 6,
        });

        map.addControl(new mapboxgl.NavigationControl(), 'top-right');

        map.on('load', () => {
            map.resize();

            // 3D budovy: Mapbox Standard ich kresli nativne (show3dObjects),
            // vlastna fill-extrusion vrstva nad 'composite' tu neexistuje a hadzala
            // 'source "composite" not found'. Standard style si to riesi sam.
            map.setConfigProperty('basemap', 'show3dObjects', true);

            onMapLoaded?.(map);
        });

        mapRef.current = map;

        // kontajner meni rozmer az po inite (sticky stlpec, lazy mount, animacia) -
        // bez ResizeObservera ostane canvas v starej velkosti a vrstvy sa
        // prekreslia az po window resize / pohybe mysou
        const ro = new ResizeObserver(() => map.resize());
        ro.observe(containerRef.current);

        return () => {
            ro.disconnect();
            map.remove();
            mapRef.current = null;
        };
    }, []);

    return (
        <div
            className="relative flex justify-center"
            style={{ width: '100%', height }}
        >
            <div
                ref={containerRef}
                style={{ width: '100%', height, minHeight: '350px' }}
                className={cn('relative z-[1]', className)}
            />
        </div>
    );
}
